/**
 * Labstep
 *
 * @module grid/SmartTableProtocolCondition/components/ToolPanel/InventoryFields/Add
 * @desc Add Inventory Field Tool Panel Component
 */

import { EntityDetailTable } from 'labstep-web/components/Entity/Detail/Table';
import { getSettingsRows } from 'labstep-web/components/ProtocolValue/Detail/Settings/utils';
import { ProtocolValueFormShowEditName } from 'labstep-web/components/ProtocolValue/Form/ShowEdit/Name';
import { ICONS } from 'labstep-web/constants/icons';
import { useGridSmartTableProtocolConditionServiceFromContext } from 'labstep-web/contexts/grid/hook';
import { ParamsStateContextProvider } from 'labstep-web/contexts/params-state';
import { ActionComponent } from 'labstep-web/core/Action/Component';
import { Flex } from 'labstep-web/core/Flex';
import { Header, SectionHeader } from 'labstep-web/core/Header';
import { Icon } from 'labstep-web/core/Icon';
import { useToolPanelContext } from 'labstep-web/grid/Index/components/ToolPanel/context';
import { getProtocolValueAmountColumnDef } from 'labstep-web/grid/SmartTableProtocolCondition/coldefs/protocol-value/amount';
import { getProtocolValueResourceColumnDef } from 'labstep-web/grid/SmartTableProtocolCondition/coldefs/protocol-value/resource';
import { getProtocolValueResourceItemColumnDef } from 'labstep-web/grid/SmartTableProtocolCondition/coldefs/protocol-value/resourceItem';
import { getProtocolValueResourceLocationColumnDef } from 'labstep-web/grid/SmartTableProtocolCondition/coldefs/protocol-value/resourceLocation';
import { getProtocolValueResourceItemStatusColDef } from 'labstep-web/grid/SmartTableProtocolCondition/coldefs/protocol-value/status';
import type { SmartTableColDef } from 'labstep-web/grid/SmartTableProtocolCondition/coldefs/types';
import { ToolPanelState } from 'labstep-web/grid/SmartTableProtocolCondition/components/ToolPanel';
import { ProtocolValueAddColumns } from 'labstep-web/grid/SmartTableProtocolCondition/components/ToolPanel/ProtocolValue/AddColumns';
import { GridSmartTableProtocolConditionService } from 'labstep-web/grid/SmartTableProtocolCondition/services/grid-smart-table-protocol-condition.service';
import { ReadOnMountHOC } from 'labstep-web/hoc/ReadOnMount';
import { ProtocolValue } from 'labstep-web/models/protocol-value.model';
import type { EntityDataGridToolPanelInventoryFieldsAddFieldProps } from './types';

export const EntityDataGridToolPanelInventoryFieldsAddField: React.FC<
  EntityDataGridToolPanelInventoryFieldsAddFieldProps
> = ({
  activeVariable,
  experimentOrProtocol,
  fullscreen,
  entityView,
  colIds,
  selectMetadata,
  setProtocolValue,
}) => {
  const toolPanelContext = useToolPanelContext();
  const gridSmartTableProtocolConditionService =
    useGridSmartTableProtocolConditionServiceFromContext();

  return (
    <ReadOnMountHOC
      type="entities"
      entityName={ProtocolValue.entityName}
      params={{
        get_single: 1,
        guid: activeVariable.guid,
      }}
      loading={{ loader: 'placeholder' }}
      children={({ entity: protocolValue }): React.ReactElement => {
        const onSuccess = (response: any) => {
          if (
            (response.response.entities?.experiment ||
              response.response.entities?.protocol) &&
            gridSmartTableProtocolConditionService
          ) {
            const colDefs: SmartTableColDef[] = [
              getProtocolValueAmountColumnDef(
                experimentOrProtocol,
                protocolValue.reportingName,
              ),
              getProtocolValueResourceItemColumnDef(
                experimentOrProtocol,
                protocolValue.reportingName,
              ),
              getProtocolValueResourceLocationColumnDef(
                experimentOrProtocol,
                protocolValue.reportingName,
              ),
              getProtocolValueResourceColumnDef(
                experimentOrProtocol,
                protocolValue.reportingName,
              ),
              getProtocolValueResourceItemStatusColDef(
                experimentOrProtocol,
                protocolValue.reportingName,
              ),
            ];

            gridSmartTableProtocolConditionService.updateColDef(
              colDefs.filter((coldef) =>
                gridSmartTableProtocolConditionService
                  .getColumnDefs()
                  .find((col) => col.colId === coldef.colId),
              ),
            );

            gridSmartTableProtocolConditionService.agGrid?.api.refreshCells(
              {
                force: true,
                suppressFlash: true,
              },
            );
            gridSmartTableProtocolConditionService.agGrid?.api.refreshHeader();
          }
        };
        return (
          <Flex column>
            <Flex vAlignContent="center" spacing="gap" shrink={0}>
              {fullscreen && (
                <ActionComponent
                  type="icon"
                  icon="arrow left"
                  text="Go Back"
                  onClick={() => {
                    setProtocolValue(protocolValue);
                    toolPanelContext?.setToolPanelState(
                      ToolPanelState.add_column,
                    );
                  }}
                />
              )}
              <Flex vAlignContent="center" spacing="gap">
                <Icon name={ICONS.protocol_value.primary} />
                <Header size="tiny" noMargin>
                  <ProtocolValueFormShowEditName
                    protocolValue={protocolValue}
                    options={{
                      onSuccess: ({ response }) => {
                        const { result, entities } = response!;
                        const protocolValue = new ProtocolValue(
                          entities.protocol_value[
                            result as string | number
                          ] as ProtocolValue,
                        );
                        if (
                          gridSmartTableProtocolConditionService &&
                          protocolValue.name
                        ) {
                          GridSmartTableProtocolConditionService.updateColIdsProtocolValueName(
                            gridSmartTableProtocolConditionService?.getColumnDefs(),
                            protocolValue?.reportingName,
                            protocolValue.name,
                            entityView,
                          );
                        }
                      },
                    }}
                  />
                </Header>
              </Flex>
            </Flex>
            <SectionHeader label="Field Settings" />
            <ParamsStateContextProvider value={{}}>
              <EntityDetailTable
                dataCy="components/ProtocolValue/Detail/Settings/Table"
                columns={[protocolValue]}
                rows={getSettingsRows(protocolValue, onSuccess)}
                noMargin
              />
            </ParamsStateContextProvider>
            <SectionHeader label="Add Columns" />
            {gridSmartTableProtocolConditionService && (
              <ProtocolValueAddColumns
                protocolValue={protocolValue}
                entityView={entityView}
                gridSmartTableProtocolConditionService={
                  gridSmartTableProtocolConditionService
                }
                colIds={colIds}
                experimentOrProtocol={experimentOrProtocol}
                selectMetadata={selectMetadata}
                setProtocolValue={setProtocolValue}
              />
            )}
          </Flex>
        );
      }}
    />
  );
};
